/**
 * views/reporting/useReportingHelpers.js - Reporting's grouping helpers:
 * engineer names to their canonical full name, and count / value by field.
 * Moved verbatim out of ReportingView.jsx; the view passes in the values
 * it reads.
 */
import { useMemo } from 'react';
import { cleanLabel, trunc, cleanValue } from './reportingHelpers';

export function useReportingHelpers({ allEngineers }) {
  // -- HELPERS -------------------------------------------

  // Map an engineer free-text value ("Ian", "Brett & Kelvin") to the canonical
  // full name of a current-team engineer if one matches by first-name. Skips
  // multi-engineer text (returns raw) and unmatched names (returns raw).
  const engineerFirstNames = useMemo(() => {
    return (allEngineers || [])
      .map((e) => ({
        first: (e.name || '').split(' ')[0].toLowerCase(),
        full: e.name,
      }))
      .filter((x) => x.first);
  }, [allEngineers]);

  const canonicalizeEngineer = (raw) => {
    if (!raw) return raw;
    const txt = String(raw).trim();
    if (!txt) return txt;
    // Multi-engineer rows: leave raw so reporting still surfaces them as a bucket
    if (/[+&/,]/.test(txt)) return txt;
    const low = txt.toLowerCase();
    for (const { first, full } of engineerFirstNames) {
      if (low === first || low === full.toLowerCase()) return full;
    }
    return txt;
  };

  const countByField = (arr, field, limit = 10) => {
    const m = {};
    const normalize = field === 'engineer' ? canonicalizeEngineer : (v) => v;
    arr.forEach((p) => {
      const raw = normalize(p[field]);
      const v = cleanLabel(raw);
      if (!v) return;
      m[v] = (m[v] || 0) + 1;
    });
    return Object.entries(m)
      .map(([name, value]) => ({ name: trunc(name), value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  };
  const valByField = (arr, field, limit = 8) => {
    const m = {};
    arr.forEach((p) => {
      const raw = p[field];
      const v = cleanLabel(raw) || cleanValue(raw);
      if (!v) return;
      const q = parseFloat(p.quotedPrice || p.quoted_price || 0);
      if (isFinite(q) && q > 0 && q < 500000000) {
        const k = trunc(v);
        m[k] = (m[k] || 0) + q;
      }
    });
    return Object.entries(m)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, limit);
  };
  return { valByField, countByField };
}
